// frontend/src/App.js
import React, { useState } from "react";
import axios from "axios";

// === Surgical change: read API base from env, fallback to your local port ===
// In production, set REACT_APP_API_BASE in frontend/.env.production
// Example: REACT_APP_API_BASE=https://api.predict.berrioslab.com
// If your ALB routes under /api, you can include it in the env value itself.
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5051";

function App() {
  const [stage, setStage] = useState("Q1");           // <-- quarter selector
  const [trainFile, setTrainFile] = useState(null);
  const [predictFile, setPredictFile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [chartSrc, setChartSrc] = useState("");       // for PNG mode
  const [fiJson, setFiJson] = useState(null);         // for JSON mode
  const [downloadLink, setDownloadLink] = useState("");

  const refreshFeatureImportance = async () => {
    // Try image first (most backends serve PNG at /feature_importance)
    const imgUrl = `${API_BASE}/feature_importance?stage=${stage}&t=${Date.now()}`;
    // We optimistically set image source, and also try fetching JSON;
    // if JSON succeeds, we’ll render the JSON list instead of the image.
    setChartSrc(imgUrl);
    setFiJson(null);

    try {
      const res = await axios.get(`${API_BASE}/feature_importance`, {
        params: { stage },
        headers: { Accept: "application/json" },
      });
      // If backend supports JSON (stage-aware sample), it’ll be { stage, feature_importance: {...} }
      if (res.data && (res.data.feature_importance || res.data.RandomForest)) {
        setFiJson(res.data.feature_importance || res.data); // normalize
        setChartSrc(""); // prefer JSON list if available
      }
    } catch {
      // ignore; PNG route will still display via <img src=...>
    }
  };

  const handleTrain = async () => {
    if (!trainFile) return;
    const formData = new FormData();
    formData.append("file", trainFile);
    formData.append("stage", stage);  // <-- pass stage

    try {
      const res = await axios.post(`${API_BASE}/train?stage=${stage}`, formData);
      // backend may return {stage, metrics: {...}} or just {...}
      const m = res.data?.metrics || res.data;
      setMetrics(m);
      await refreshFeatureImportance();
    } catch (err) {
      alert("Training failed: " + (err.response?.data?.error || err.message));
    }
  };

  const handleBatchPredict = async () => {
    if (!predictFile) return;
    const formData = new FormData();
    formData.append("file", predictFile);
    formData.append("stage", stage);  // <-- pass stage

    try {
      await axios.post(`${API_BASE}/batch_predict?stage=${stage}`, formData);
      setDownloadLink(`${API_BASE}/download_predictions`);
    } catch (err) {
      alert("Prediction failed: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div style={{ padding: 20, fontFamily: "Arial" }}>
      <h1>📊 Grade Prediction App</h1>

      {/* Quarter selector */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ marginRight: 8 }}>Quarter:</label>
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="Q1">Q1</option>
          <option value="Q2">Q2</option>
          {/* add Q3/Q4 if you wire them in the backend */}
        </select>
      </div>

      <h3>1. Upload CSV to Train</h3>
      <input type="file" onChange={(e) => setTrainFile(e.target.files[0])} />
      <button onClick={handleTrain} style={{ marginLeft: 8 }}>Train Model</button>

      {metrics?.RandomForest && (
        <div style={{ marginTop: 20 }}>
          <h4>Model Performance (Random Forest) — {stage}</h4>
          <p>R² Score: {metrics.RandomForest.r2_score}</p>
          <p>MAE: {metrics.RandomForest.mae}</p>
          <p>MSE: {metrics.RandomForest.mse}</p>
        </div>
      )}

      {/* Feature Importance: prefer JSON list if available; else PNG */}
      {fiJson && (
        <div style={{ marginTop: 20 }}>
          <h4>Feature Importance — {stage}</h4>
          <ol>
            {Object.entries(fiJson)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 10)
              .map(([feat, imp]) => (
                <li key={feat}>
                  {feat}: {typeof imp === "number" ? imp.toFixed(4) : imp}
                </li>
              ))}
          </ol>
        </div>
      )}

      {!fiJson && chartSrc && (
        <div style={{ marginTop: 20 }}>
          <h4>Feature Importance — {stage}</h4>
          <img
            src={chartSrc}
            alt="Feature Importances"
            style={{ maxWidth: "100%", border: "1px solid #ccc", padding: "8px" }}
          />
        </div>
      )}

      <h3 style={{ marginTop: 40 }}>2. Upload CSV for Batch Prediction</h3>
      <input type="file" onChange={(e) => setPredictFile(e.target.files[0])} />
      <button onClick={handleBatchPredict} style={{ marginLeft: 8 }}>
        Generate Predictions
      </button>

      {downloadLink && (
        <div style={{ marginTop: 20 }}>
          <a href={downloadLink} download>
            📥 Download Predictions CSV
          </a>
        </div>
      )}
    </div>
  );
}

export default App;
